/**
 * "Coming up" — the next handful of scheduled blocks in your day or
 * tomorrow. Surfaces on the home screen so the calendar feature is
 * visible without forcing a navigation. Empty state is its own
 * affordance: "Connect a calendar".
 *
 * Pulls from two sources:
 *   - GET /api/tasks            scheduled + proposed personal tasks
 *   - GET /api/calendars/events busy windows from linked calendars
 *
 * Combined, sorted by start time, capped to the next ~5 entries
 * inside the next 36 hours. Click a task row → /calendar.
 */

import { useEffect, useState } from 'react';
import { api, type PersonalTask } from '../api';
import { Link, useRouter } from '../router';
import { UiNode } from '../lib/UiNode';
import { IconCalendar } from './Icon';

interface BusyRow {
  kind: 'busy';
  startsAt: number;
  endsAt: number;
  title: string | null;
}
interface TaskRow {
  kind: 'task';
  task: PersonalTask;
  startsAt: number;
  endsAt: number;
}
type Row = BusyRow | TaskRow;

export function ComingUp() {
  const { navigate } = useRouter();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [hasCalendars, setHasCalendars] = useState<boolean | null>(null);

  async function load(): Promise<void> {
    const now = Math.floor(Date.now() / 1000);
    const horizon = now + 36 * 3600;
    try {
      const [tasksRes, eventsRes, calsRes] = await Promise.all([
        api.listPersonalTasks(),
        api.listCalendarEvents(now, horizon).catch(() => ({ events: [] })),
        api.listCalendars().catch(() => ({ links: [] })),
      ]);
      setHasCalendars(calsRes.links.length > 0);
      const busyRows: Row[] = eventsRes.events
        .filter((e) => e.busy && !e.allDay && e.endsAt > now && e.startsAt < horizon)
        .map((e) => ({
          kind: 'busy',
          startsAt: e.startsAt,
          endsAt: e.endsAt,
          title: e.title,
        }));
      const taskRows: Row[] = tasksRes.tasks
        .filter(
          (t) =>
            (t.status === 'scheduled' || t.status === 'proposed') &&
            t.scheduledStartsAt !== null &&
            t.scheduledEndsAt !== null &&
            t.scheduledEndsAt > now &&
            t.scheduledStartsAt < horizon,
        )
        .map((t) => ({
          kind: 'task',
          task: t,
          startsAt: t.scheduledStartsAt!,
          endsAt: t.scheduledEndsAt!,
        }));
      const combined = [...busyRows, ...taskRows]
        .sort((a, b) => a.startsAt - b.startsAt)
        .slice(0, 5);
      setRows(combined);
    } catch {
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
    const onChange = () => void load();
    window.addEventListener('synfolio:tasks-changed', onChange);
    return () => window.removeEventListener('synfolio:tasks-changed', onChange);
  }, []);

  // Hide the widget until we know whether to show empty state or not.
  if (rows === null) return null;

  const empty = rows.length === 0;
  const noCalendars = hasCalendars === false && empty;

  return (
    <UiNode
      as="section"
      name="home-coming-up"
      entity="coming_up"
      className="panel flex flex-col gap-2 p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium">
          <IconCalendar size={14} />
          Coming up
        </h2>
        <Link to="/calendar" className="text-[11px] text-cream-3 hover:text-cream">
          Open calendar →
        </Link>
      </div>
      {noCalendars ? (
        <div className="flex flex-col items-start gap-2 py-1 text-xs text-cream-3">
          <span>
            Connect a calendar so SynFolio knows when you're free, then create your first
            task.
          </span>
          <div className="flex gap-2">
            <Link to="/profile" className="btn btn-ghost text-xs">
              Connect a calendar
            </Link>
            <Link to="/calendar" className="btn btn-primary text-xs">
              New task
            </Link>
          </div>
        </div>
      ) : empty ? (
        <p className="py-1 text-xs text-cream-3">
          Nothing on your calendar in the next 36 hours.{' '}
          <Link to="/calendar" className="text-cream-2 underline-offset-2 hover:underline">
            Add a task →
          </Link>
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((r, i) => (
            <li key={i}>
              {r.kind === 'task' ? (
                <UiNode
                  as="button"
                  name="home-coming-up-row"
                  entity="task"
                  entityId={r.task.id}
                  action="open_edit"
                  state={r.task.status === 'proposed' ? 'proposed' : 'scheduled'}
                  type="button"
                  className="row-nudge flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-ink-3"
                  onClick={() => navigate('/calendar')}
                >
                  <span className="w-20 flex-shrink-0 text-[11px] text-cream-3">
                    {formatWhen(r.startsAt)}
                  </span>
                  <span
                    aria-hidden
                    className="h-2 w-2 flex-shrink-0 rounded-full"
                    style={{
                      background:
                        r.task.workLevel === 'deep'
                          ? 'var(--color-clove)'
                          : r.task.workLevel === 'shallow'
                            ? 'var(--color-leaf)'
                            : 'var(--color-mist)',
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{r.task.title}</span>
                  {r.task.status === 'proposed' && (
                    <span className="chip chip-accent text-[10px]">proposed</span>
                  )}
                </UiNode>
              ) : (
                <UiNode
                  as="div"
                  name="home-coming-up-row"
                  entity="task"
                  state="busy"
                  className="flex w-full items-center gap-3 rounded-lg p-2"
                >
                  <span className="w-20 flex-shrink-0 text-[11px] text-cream-3">
                    {formatWhen(r.startsAt)}
                  </span>
                  <span
                    aria-hidden
                    className="h-2 w-2 flex-shrink-0 rounded-full bg-ash"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-cream-2">
                    {r.title ?? 'Busy'}
                  </span>
                </UiNode>
              )}
            </li>
          ))}
        </ul>
      )}
    </UiNode>
  );
}

function formatWhen(unix: number): string {
  const d = new Date(unix * 1000);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  const tom = new Date(now);
  tom.setDate(now.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tom.getFullYear() &&
    d.getMonth() === tom.getMonth() &&
    d.getDate() === tom.getDate();
  if (isTomorrow) return `Tomorrow ${time}`;
  return d.toLocaleDateString(undefined, { weekday: 'short' }) + ' ' + time;
}
